import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import DescriptionIcon from "@mui/icons-material/Description";
import SendIcon from "@mui/icons-material/Send";
import PropTypes from "prop-types";

const CreateRequestDialog = ({
  open,
  onClose,
  mode = "create",
  request = null,
}) => {
  const [formData, setFormData] = useState({
    requestId:
      request?.id || Math.floor(10000 + Math.random() * 90000).toString(),
    title: request?.title || "",
    description: request?.description || "",
    file: null,
    // Sample attachments - replace with actual data from your backend
    attachments: request?.attachments || [
      { name: "document.pdf", type: "pdf", size: "2.5 MB" },
      { name: "image.jpg", type: "image", size: "1.2 MB" },
      { name: "report.docx", type: "doc", size: "3.1 MB" },
      { name: "presentation.pptx", type: "ppt", size: "4.5 MB" },
    ],
    // Sample comments - replace with actual data from your backend
    comments: request?.comments || [
      {
        id: 1,
        user: "John Doe",
        avatar: "JD",
        text: "Initial review completed. Moving to next phase.",
        timestamp: "2024-03-20T10:30:00",
      },
      {
        id: 2,
        user: "Jane Smith",
        avatar: "JS",
        text: "Please provide additional details about the artwork dimensions.",
        timestamp: "2024-03-20T11:45:00",
      },
    ],
  });

  const [newComment, setNewComment] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      file: e.target.files[0],
    }));
  };

  const handleSubmit = () => {
    // Handle form submission here
    onClose();
  };

  const handleAddComment = () => {
    if (!newComment.trim()) return;

    const comment = {
      id: Date.now(),
      user: "Current User", // Replace with actual user data
      avatar: "CU",
      text: newComment,
      timestamp: new Date().toISOString(),
    };

    setFormData((prev) => ({
      ...prev,
      comments: [comment, ...prev.comments],
    }));
    setNewComment("");
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const getFileIcon = (type) => {
    switch (type) {
      case "pdf":
        return <PictureAsPdfIcon sx={{ fontSize: 40, color: "#f44336" }} />;
      case "image":
        return <ImageIcon sx={{ fontSize: 40, color: "#4caf50" }} />;
      case "doc":
        return <DescriptionIcon sx={{ fontSize: 40, color: "#2196f3" }} />;
      case "ppt":
        return <DescriptionIcon sx={{ fontSize: 40, color: "#ff9800" }} />;
      default:
        return <InsertDriveFileIcon sx={{ fontSize: 40, color: "#757575" }} />;
    }
  };

  const renderField = (label, value, name, multiline = false) => {
    if (mode === "view") {
      return (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {label}
          </Typography>
          <Typography variant="body1">{value}</Typography>
        </Box>
      );
    }

    return (
      <TextField
        label={label}
        name={name}
        value={value}
        onChange={handleChange}
        fullWidth
        required
        multiline={multiline}
        rows={multiline ? 4 : 1}
      />
    );
  };

  const renderAttachments = () => {
    if (mode !== "view" || !formData.attachments?.length) return null;

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Attachments
        </Typography>
        <Grid container spacing={2}>
          {formData.attachments.map((file) => (
            <Grid item xs={12} sm={6} md={3} key={`${file.name}-${file.type}`}>
              <Card
                sx={{
                  height: "100%",
                  cursor: "pointer",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: 3,
                    transition: "all 0.2s ease-in-out",
                  },
                }}
                onClick={() => window.open(`#${file.name}`, "_blank")}
              >
                <CardContent
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    p: 2,
                  }}
                >
                  {getFileIcon(file.type)}
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      width: "100%",
                    }}
                  >
                    {file.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {file.size}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  const renderComments = () => {
    if (mode !== "view") return null;

    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Comments
        </Typography>

        <List>
          {formData.comments.map((comment) => (
            <ListItem key={comment.id} alignItems="flex-start" sx={{ px: 0 }}>
              <ListItemAvatar>
                <Avatar>{comment.avatar}</Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="subtitle2">{comment.user}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatTimestamp(comment.timestamp)}
                    </Typography>
                  </Box>
                }
                secondary={
                  <Typography
                    variant="body2"
                    color="text.primary"
                    sx={{ mt: 0.5 }}
                  >
                    {comment.text}
                  </Typography>
                }
              />
            </ListItem>
          ))}
        </List>

        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            multiline
            rows={2}
            placeholder="Add a comment..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            sx={{ mb: 1 }}
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              endIcon={<SendIcon />}
              onClick={handleAddComment}
              disabled={!newComment.trim()}
            >
              Add Comment
            </Button>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {mode === "create" ? "Create New Request" : "Request Details"}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ padding: "20px" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
          {renderField("Request ID", formData.requestId, "requestId")}
          {renderField("Request Title", formData.title, "title")}
          {renderField(
            "Request Description",
            formData.description,
            "description",
            true
          )}

          {mode === "create" && (
            <Button variant="outlined" component="label" fullWidth>
              Upload File
              <input type="file" hidden onChange={handleFileChange} />
            </Button>
          )}
          {formData.file && (
            <Box sx={{ mt: 1 }}>Selected file: {formData.file.name}</Box>
          )}
          {renderAttachments()}
          {renderComments()}
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: "20px" }}>
        <Button onClick={onClose}>
          {mode === "create" ? "Cancel" : "Close"}
        </Button>
        {mode === "create" && (
          <Button onClick={handleSubmit} variant="contained">
            Submit
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

CreateRequestDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(["create", "view"]),
  request: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    status: PropTypes.string,
    attachments: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
        size: PropTypes.string.isRequired,
      })
    ),
    comments: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        user: PropTypes.string.isRequired,
        avatar: PropTypes.string.isRequired,
        text: PropTypes.string.isRequired,
        timestamp: PropTypes.string.isRequired,
      })
    ),
  }),
};

CreateRequestDialog.defaultProps = {
  mode: "create",
  request: null,
};

export default CreateRequestDialog;
